import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BookX, HandCoins, Banknote, PackageCheck, Landmark, ChevronRight, CheckCheck } from "lucide-react";
import { Sheet, SheetContent } from "./ui/sheet";
import { http } from "../lib/api";
import { toast } from "sonner";

// Programmatically open a hidden bell's dialog by clicking its trigger. The
// real bell components stay mounted (display:none on mobile) so their polling,
// chimes and OS notifications keep running in the background.
const clickBell = (testid) => {
  const el = document.querySelector(`[data-testid="${testid}"]`);
  if (el) el.click();
};

// Single consolidated notification bell for mobile. Shows one aggregate badge
// and a tidy panel; each row routes to the matching existing bell/dialog.
export default function NotificationHub({ counts = {}, isAdmin, isHqUser, needsClosing }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const total = Object.values(counts).reduce((a, b) => a + (b || 0), 0);

  const rows = [
    needsClosing && { key: "book", icon: BookX, tone: "text-rose-600 bg-rose-50", label: "Tutup Buku", desc: "Pengingat akhir hari operasional", count: counts.book || 0, action: () => navigate("/admin/shift") },
    { key: "transfer", icon: Landmark, tone: "text-amber-600 bg-amber-50", label: "Verifikasi Transfer", desc: "Cek m-banking PT Emerald Valasindo", count: counts.transfer || 0, action: () => clickBell("transfer-notif-bell") },
    { key: "valas", icon: Banknote, tone: "text-sky-600 bg-sky-50", label: isHqUser ? "Permintaan Valas Cabang" : "Status Permintaan Valas", desc: isHqUser ? "Setujui / tolak permintaan valas" : "Pantau status permintaan Anda", count: counts.valas || 0, action: () => clickBell("valas-request-bell") },
    isAdmin && { key: "modal", icon: HandCoins, tone: "text-emerald-600 bg-emerald-50", label: "Permintaan Modal Cabang", desc: "Setujui kiriman modal ke cabang", count: counts.capital || 0, action: () => clickBell("capital-bell") },
    isHqUser && { key: "eod", icon: PackageCheck, tone: "text-emerald-600 bg-emerald-50", label: "Setoran EOD Cabang", desc: "Rekonsiliasi stok masuk ke Pusat", count: counts.hq || 0, action: () => clickBell("hq-arrivals-bell") },
  ].filter(Boolean);

  const go = (action) => { setOpen(false); setTimeout(() => action(), 180); };

  const [marking, setMarking] = useState(false);
  const markAllChecked = async () => {
    setMarking(true);
    try {
      const { data } = await http.post("/transfer-notifications/ack-all");
      window.dispatchEvent(new Event("emc:transfer-refresh"));
      toast.success(data?.acknowledged ? `${data.acknowledged} transfer ditandai sudah dicek` : "Semua transfer sudah dicek");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menandai");
    } finally { setMarking(false); }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="notif-hub-bell"
        aria-label="Pusat Notifikasi"
        className="relative inline-flex items-center justify-center h-9 w-9 rounded-md border border-slate-200 bg-white/60 text-slate-600 hover:bg-slate-100 transition active:scale-90">
        <Bell size={18} className={total > 0 ? "text-rose-600" : "text-slate-500"} />
        {total > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center">
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-[88%] max-w-sm p-0 flex flex-col" data-testid="notif-hub-panel">
          <div className="px-5 py-4 border-b border-slate-200">
            <div className="flex items-center gap-2 text-slate-900">
              <Bell size={18} /><span className="font-semibold">Pusat Notifikasi</span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">{total > 0 ? `${total} butuh perhatian` : "Tidak ada yang mendesak"}</div>
            {(counts.transfer || 0) > 0 && (
              <button type="button" onClick={markAllChecked} disabled={marking} data-testid="notif-mark-all-checked"
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-60 active:scale-95 transition">
                <CheckCheck size={14} /> {marking ? "Menandai…" : "Tandai semua transfer sudah dicek"}
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {rows.map((r) => {
              const Icon = r.icon;
              return (
                <button key={r.key} type="button" onClick={() => go(r.action)} data-testid={`notif-hub-row-${r.key}`}
                  className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-slate-50 transition">
                  <span className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${r.tone}`}><Icon size={17} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-slate-800 leading-tight">{r.label}</span>
                    <span className="block text-xs text-slate-500 leading-tight mt-0.5">{r.desc}</span>
                  </span>
                  {r.count > 0 && (
                    <span className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center">{r.count > 99 ? "99+" : r.count}</span>
                  )}
                  <ChevronRight size={16} className="shrink-0 text-slate-300" />
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
